import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { ui } from "../../../shared/styles/uiSystem"
import { apiFetch } from "../../../utils/apiFetch"

const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

export default function PatientFormDrawer({
  open,
  mode = "create",
  form,
  setForm,
  onClose,
  onSubmit,
  onUseDuplicatePatient,
  saving = false,
  isReadOnly = false,
  readOnlyReason = ""
}) {
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440
  )
  const [checkingDuplicates, setCheckingDuplicates] = useState(false)
  const [duplicateCandidates, setDuplicateCandidates] = useState([])

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  useEffect(() => {
    if (!open) {
      setDuplicateCandidates([])
      setCheckingDuplicates(false)
    }
  }, [open])

  if (!open) return null

  const isEdit = mode === "edit"
  const isMobile = viewportWidth < 700

  const title = isEdit ? "Editar paciente" : "Nuevo paciente"
  const subtitle = isEdit
    ? "Actualiza los datos principales del paciente"
    : "Alta rápida y limpia del paciente"

  const actionLabel = saving || checkingDuplicates
    ? checkingDuplicates
      ? "Revisando..."
      : isEdit
        ? "Guardando..."
        : "Creando..."
    : isEdit
      ? "Guardar cambios"
      : "Crear paciente"

  const getDuplicateCandidates = async () => {
    if (isEdit) return []

    const firstName = String(form?.firstName || "").trim()
    const lastName = String(form?.lastName || "").trim()
    const phone = String(form?.phone || "").trim()
    const email = String(form?.email || "").trim()
    const birthDate = String(form?.birthDate || "").trim()

    if (!firstName && !lastName && !phone && !email) return []

    const params = new URLSearchParams()

    if (firstName) params.set("firstName", firstName)
    if (lastName) params.set("lastName", lastName)
    if (phone) params.set("phone", phone)
    if (email) params.set("email", email)
    if (birthDate) params.set("birthDate", birthDate)

    const res = await apiFetch(`${API_BASE}/patients/duplicate-candidates?${params.toString()}`)
    const data = await res.json().catch(() => null)

    if (!res.ok) return []

    return Array.isArray(data?.candidates) ? data.candidates : []
  }

  const handleSubmitClick = async () => {
    if (saving || checkingDuplicates || isReadOnly) return

    if (isEdit) {
      await onSubmit?.()
      return
    }

    try {
      setCheckingDuplicates(true)

      const candidates = await getDuplicateCandidates()
      const strongCandidates = candidates.filter((item) =>
        ["HIGH", "MEDIUM"].includes(String(item.severity || "").toUpperCase())
      )

      if (strongCandidates.length) {
        setDuplicateCandidates(strongCandidates)
        return
      }

      await onSubmit?.()
    } finally {
      setCheckingDuplicates(false)
    }
  }

  const handleCreateAnyway = async () => {
    setDuplicateCandidates([])
    await onSubmit?.({ bypassDuplicateWarning: true })
  }

  const handleUseExistingPatient = async (candidate) => {
    if (!candidate?.id || typeof onUseDuplicatePatient !== "function") return

    setDuplicateCandidates([])
    await onUseDuplicatePatient(candidate)
  }

  const content = (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,23,42,0.48)",
          zIndex: 999999,
          display: "flex",
          justifyContent: "flex-end",
          backdropFilter: "blur(4px)"
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: isMobile ? "100%" : 540,
            maxWidth: "100%",
            height: "100%",
            background: "#ffffff",
            boxShadow: "-18px 0 40px rgba(15,23,42,0.22)",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            zIndex: 1000000
          }}
        >
          <div
            style={{
              padding: 18,
              borderBottom: "1px solid #e2e8f0",
              background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
              position: "sticky",
              top: 0,
              zIndex: 2
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "flex-start",
                flexWrap: "wrap"
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    display: "inline-flex",
                    padding: "6px 10px",
                    borderRadius: 999,
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    color: "#1d4ed8",
                    fontSize: 10,
                    fontWeight: 900,
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    marginBottom: 10
                  }}
                >
                  Paciente
                </div>

                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 900,
                    color: "#0f172a",
                    lineHeight: 1.02,
                    letterSpacing: "-0.03em"
                  }}
                >
                  {title}
                </div>

                <div
                  style={{
                    fontSize: 13,
                    color: "#64748b",
                    marginTop: 6,
                    lineHeight: 1.4,
                    maxWidth: 360
                  }}
                >
                  {subtitle}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  justifyContent: isMobile ? "stretch" : "flex-end",
                  width: isMobile ? "100%" : "auto"
                }}
              >
                <button
                  onClick={onClose}
                  style={{
                    ...ui.secondaryButton,
                    flex: isMobile ? 1 : "none"
                  }}
                  disabled={saving || checkingDuplicates}
                >
                  Cerrar
                </button>

                <button
                  onClick={handleSubmitClick}
                  style={{
                    ...ui.primaryButton,
                    flex: isMobile ? 1 : "none",
                    opacity: saving || checkingDuplicates || isReadOnly ? 0.7 : 1,
                    cursor: saving || checkingDuplicates || isReadOnly ? "not-allowed" : "pointer"
                  }}
                  disabled={saving || checkingDuplicates || isReadOnly}
                >
                  {actionLabel}
                </button>
              </div>
            </div>
          </div>

          <div
            style={{
              padding: 18,
              overflowY: "auto",
              flex: 1,
              display: "grid",
              gap: 16,
              background: "#f8fafc"
            }}
          >
            {isReadOnly && (
              <div
                style={{
                  ...ui.dangerCard,
                  padding: 12,
                  fontWeight: 700
                }}
              >
                 {readOnlyReason || "Modo solo lectura"}
              </div>
            )}

            <div
              style={{
                ...ui.card,
                padding: 16
              }}
            >
              <div style={ui.sectionTitle}>Datos principales</div>
              <div style={{ ...ui.metaText, marginTop: 2, marginBottom: 14 }}>
                Captura mínima para alta o edición rápida del paciente
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: 12
                }}
              >
                <Field label="Nombre">
                  <input
                    value={form.firstName}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, firstName: e.target.value }))
                    }
                    placeholder="Nombre"
                    style={ui.input}
                    disabled={saving || checkingDuplicates || isReadOnly}
                  />
                </Field>

                <Field label="Apellido">
                  <input
                    value={form.lastName}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, lastName: e.target.value }))
                    }
                    placeholder="Apellido"
                    style={ui.input}
                    disabled={saving || checkingDuplicates || isReadOnly}
                  />
                </Field>

                <Field label="Teléfono">
                  <input
                    value={form.phone}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, phone: e.target.value }))
                    }
                    placeholder="Teléfono"
                    style={ui.input}
                    disabled={saving || checkingDuplicates || isReadOnly}
                  />
                </Field>

                <Field label="Correo">
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    placeholder="Correo"
                    style={ui.input}
                    disabled={saving || checkingDuplicates || isReadOnly}
                  />
                </Field>

                <Field label="Fecha de nacimiento">
                  <input
                    type="date"
                    value={form.birthDate}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, birthDate: e.target.value }))
                    }
                    style={ui.input}
                    disabled={saving || checkingDuplicates || isReadOnly}
                  />
                </Field>
              </div>
            </div>

            <div
              style={{
                ...ui.infoCard,
                padding: 14
              }}
            >
              <div style={{ fontWeight: 800, marginBottom: 6 }}>
                Recomendación
              </div>
              <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.45 }}>
                Captura lo esencial para no frenar el flujo. El resto del expediente
                se completa después desde la ficha del paciente.
              </div>
            </div>
          </div>
        </div>
      </div>

      {duplicateCandidates.length ? (
        <DuplicatePatientsModal
          candidates={duplicateCandidates}
          canUseExisting={typeof onUseDuplicatePatient === "function"}
          onCancel={() => setDuplicateCandidates([])}
          onCreateAnyway={handleCreateAnyway}
          onUseExisting={handleUseExistingPatient}
          isMobile={isMobile}
        />
      ) : null}
    </>
  )

  return createPortal(content, document.body)
}

function DuplicatePatientsModal({
  candidates,
  canUseExisting,
  onCancel,
  onCreateAnyway,
  onUseExisting,
  isMobile
}) {
  const top = candidates[0] || {}
  const fullName = top.fullName || `${top.firstName || ""} ${top.lastName || ""}`.trim()
  const severity = String(top.severity || "").toUpperCase()
  const isHigh = severity === "HIGH"

  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000002,
        background: "rgba(15, 23, 42, 0.58)",
        display: "grid",
        placeItems: "center",
        padding: 18,
        backdropFilter: "blur(5px)"
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          width: isMobile ? "100%" : 560,
          maxWidth: "100%",
          borderRadius: 24,
          background: "#ffffff",
          boxShadow: "0 28px 80px rgba(15,23,42,0.34)",
          border: isHigh ? "2px solid #f97316" : "2px solid #facc15",
          overflow: "hidden"
        }}
      >
        <div
          style={{
            padding: 18,
            background: isHigh
              ? "linear-gradient(135deg, #fff7ed 0%, #fef2f2 100%)"
              : "linear-gradient(135deg, #fefce8 0%, #fff7ed 100%)",
            borderBottom: isHigh ? "1px solid #fed7aa" : "1px solid #fde68a"
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              minHeight: 28,
              padding: "0 10px",
              borderRadius: 999,
              background: isHigh ? "#ffedd5" : "#fef3c7",
              color: isHigh ? "#c2410c" : "#92400e",
              fontSize: 11,
              fontWeight: 950,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 10
            }}
          >
            {isHigh ? "Coincidencia fuerte" : "Posible duplicado"}
          </div>

          <div
            style={{
              fontSize: 24,
              lineHeight: 1.05,
              fontWeight: 950,
              letterSpacing: "-0.035em",
              color: "#0f172a"
            }}
          >
            Este paciente podría estar duplicado
          </div>

          <div
            style={{
              marginTop: 8,
              fontSize: 14,
              color: "#475569",
              lineHeight: 1.45,
              fontWeight: 650
            }}
          >
            Antes de crear un nuevo registro, revisa si corresponde al paciente existente.
            Esto evita duplicar citas, pagos, tratamientos e historial clínico.
          </div>
        </div>

        <div style={{ padding: 18, display: "grid", gap: 14 }}>
          <div
            style={{
              borderRadius: 18,
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
              padding: 14,
              display: "grid",
              gap: 8
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 950,
                color: "#0f172a",
                lineHeight: 1.15
              }}
            >
              {fullName || "Paciente existente"}
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                fontSize: 12.5,
                color: "#475569",
                fontWeight: 750
              }}
            >
              {top.phone ? <span>Tel: {top.phone}</span> : null}
              {top.email ? <span>Correo: {top.email}</span> : null}
            </div>

            {Array.isArray(top.reasons) && top.reasons.length ? (
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  flexWrap: "wrap",
                  marginTop: 4
                }}
              >
                {top.reasons.map((reason) => (
                  <span
                    key={reason}
                    style={{
                      minHeight: 26,
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "0 9px",
                      borderRadius: 999,
                      background: "#fff7ed",
                      border: "1px solid #fed7aa",
                      color: "#9a3412",
                      fontSize: 11.5,
                      fontWeight: 850
                    }}
                  >
                    {reason}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          {candidates.length > 1 ? (
            <div
              style={{
                fontSize: 12,
                color: "#64748b",
                fontWeight: 750,
                lineHeight: 1.4
              }}
            >
              Hay {candidates.length} coincidencias posibles. Se muestra primero la más fuerte.
            </div>
          ) : null}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 4
            }}
          >
            <button
              type="button"
              onClick={onCancel}
              style={{
                ...ui.secondaryButton,
                minHeight: 38
              }}
            >
              Cancelar, no crear
            </button>

            {canUseExisting ? (
              <button
                type="button"
                onClick={() => onUseExisting(top)}
                style={{
                  ...ui.primaryButton,
                  minHeight: 38,
                  background: "linear-gradient(135deg, #2563eb, #4f46e5)"
                }}
              >
                Usar paciente existente
              </button>
            ) : null}

            <button
              type="button"
              onClick={onCreateAnyway}
              style={{
                ...ui.secondaryButton,
                minHeight: 38,
                background: "#fff7ed",
                border: "1px solid #fdba74",
                color: "#9a3412"
              }}
            >
              Crear de todos modos
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          color: "#64748b",
          fontWeight: 800,
          marginBottom: 6
        }}
      >
        {label}
      </div>
      {children}
    </div>
  )
}
